// types and consts
import {
  ENVIRONMENTAL_FOOTER_ICON_OPTIONS,
  ENVIRONMENTAL_FOOTER_OPTIONS,
} from '@components/EmailSignaturev2/consts';
// components
import EmailSignatureIcon from '@components/EmailSignaturev2/EmailSignatureIcon';
import {
  type EmailSignatureBasicSelectOption,
  type EmailSignatureIconName,
  SideEditorNodeKey,
} from '@components/EmailSignaturev2/types';
import { FormSelector, FormSelectorGroup } from '@components/FormSelector/FormSelector';
import Input from '@components/Input/Input';
import { SideEditorScrollType } from '@components/SideEditor/SideEditor';
import { useSideEditorContext } from '@components/SideEditor/utils';
import Tooltip from '@components/Tooltip/Tooltip';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
import { faQuestionCircle, faTrash } from '@fortawesome/pro-light-svg-icons';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// hooks and context
import useNode from '@hooks/use-node';
// utils
import { generateNewEnvironmentalFooterId } from '@modules/email-signatures-helpers';
import Emitter from '@modules/event-emitter';
import { debounce } from '@modules/helpers';
import { type FC, useContext, useEffect, useRef, useState } from 'react';
import Select from 'react-select';

import EmailSignatureLockLayer from '../EmailSignatureLockLayer';
import {
  EmailSignatureEditorAddonsBox,
  EmailSignatureEditorAddonsBoxBody,
  EmailSignatureEditorAddonsBoxHeader,
  EmailSignatureEditorAddonsBoxHeaderContents,
  EmailSignatureEditorAddonsBoxHeaderDivider,
} from './EmailSignatureEditorAddonBox';
import styles from './EmailSignatureEditorAddons.module.scss';
import environmentalStyles from './EmailSignatureEditorAddonsEnvironmentalBox.module.scss';

const EmailSignatureEditorAddonsEnvironmentalBox: FC<React.PropsWithChildren<unknown>> = () => {
  const { scrollIntoView, registerNode, panelIndex } = useSideEditorContext();
  const { isInTransitionState, emailSignature, emailSignatureDispatch, isSignatureLocked } = useContext(
    emailSignatureSideEditorServicesContext,
  );
  const [menuPortalTarget, setMenuPortalTarget] = useState<HTMLElement>();
  const [selectedFooterOption, setSelectedFooterOption] = useState<EmailSignatureBasicSelectOption | null>(
    ENVIRONMENTAL_FOOTER_OPTIONS[0],
  );
  const [environmentalBox, environmentalBoxRef] = useNode<HTMLDivElement>();
  const debouncedShowDummyEnvironmentalFooter = useRef(
    debounce(() => {
      Emitter.emit('SHOW_DUMMY_ENVIRONMENTAL_FOOTER');
    }, 200),
  );

  const debouncedRemoveDummyEnvironmentalFooter = useRef(
    debounce(() => {
      Emitter.emit('REMOVE_DUMMY_ENVIRONMENTAL_FOOTER');
    }, 200),
  );

  useEffect(() => setMenuPortalTarget(document.body), []);

  useEffect(() => {
    if (!environmentalBox) return;
    registerNode(SideEditorNodeKey.ENVIRONMENTAL_FOOTER_BOX_NODE, {
      panelBelongTo: panelIndex,
      node: environmentalBox,
    });
  }, [environmentalBox, panelIndex, registerNode]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    const handleScrollToSocialLinks = () => {
      if (isInTransitionState) {
        return;
      }
      scrollIntoView(SideEditorNodeKey.ENVIRONMENTAL_FOOTER_BOX_NODE, SideEditorScrollType.START);
    };
    Emitter.on('SCROLL_TO_ENVIRONMENTAL_FOOTER', handleScrollToSocialLinks);

    return () => {
      Emitter.off('SCROLL_TO_ENVIRONMENTAL_FOOTER', handleScrollToSocialLinks);
    };
  }, [isInTransitionState, panelIndex, scrollIntoView]);

  const handleAddEnvironmentalFooter = () => {
    emailSignatureDispatch({
      type: 'ADD_ENVIRONMENTAL_FOOTER',
      value: {
        id: generateNewEnvironmentalFooterId(),
        baseIcon: ENVIRONMENTAL_FOOTER_ICON_OPTIONS[0],
        iconUrl: '',
        value: ENVIRONMENTAL_FOOTER_OPTIONS[0].value,
      },
    });
  };

  const handleRemoveEnvironmentalFooter = () => {
    emailSignatureDispatch({
      type: 'DELETE_ENVIRONMENTAL_FOOTER',
    });
    setSelectedFooterOption(ENVIRONMENTAL_FOOTER_OPTIONS[0]);
  };

  const handleSelectedFooterOptionChange = (selectedFooterOption: EmailSignatureBasicSelectOption | null) => {
    emailSignatureDispatch({
      type: 'UPDATE_ENVIRONMENTAL_FOOTER_CONTENT',
      value: selectedFooterOption?.value ?? '',
    });
    setSelectedFooterOption(selectedFooterOption);
  };

  const handleEnvironmentalFooterValueChange = (newValue: string) => {
    emailSignatureDispatch({
      type: 'UPDATE_ENVIRONMENTAL_FOOTER_CONTENT',
      value: newValue,
    });
    // Set to CUSTOM option
    setSelectedFooterOption(ENVIRONMENTAL_FOOTER_OPTIONS[ENVIRONMENTAL_FOOTER_OPTIONS.length - 1]);
  };

  const handleEnvironmentalFooterBaseIconChange = (newBaseIcon: string) => {
    emailSignatureDispatch({
      type: 'UPDATE_ENVIRONMENTAL_FOOTER_ICON',
      value: newBaseIcon as EmailSignatureIconName,
    });
  };

  return (
    <EmailSignatureEditorAddonsBox ref={environmentalBoxRef}>
      <EmailSignatureEditorAddonsBoxHeader>
        <EmailSignatureEditorAddonsBoxHeaderContents>
          <EmailSignatureIcon
            inlineStyle={{ color: '#3db512', fontSize: '1.625rem' }}
            icon={emailSignature.environmentalFooter?.baseIcon ?? 'leaf'}
          />
          <span>Environmental footer</span>
          {!emailSignature.environmentalFooter && (
            <Tooltip
              label={'Prompt recipients to not print the email with an environmental footer'}
              aria-label={'addon-environmental-info'}
            >
              <span>
                <button
                  onMouseEnter={() => debouncedShowDummyEnvironmentalFooter.current()}
                  onMouseLeave={() => debouncedRemoveDummyEnvironmentalFooter.current()}
                  onFocus={() => debouncedShowDummyEnvironmentalFooter.current()}
                  onBlur={() => debouncedRemoveDummyEnvironmentalFooter.current()}
                  className={styles['addons-box-header-info-button']}
                >
                  <FontAwesomeIcon size="1x" icon={faQuestionCircle} />
                </button>
              </span>
            </Tooltip>
          )}
        </EmailSignatureEditorAddonsBoxHeaderContents>
        {!emailSignature.environmentalFooter && (
          <Tooltip
            size="small"
            label="You cannot add an environmental footer, please contact your admin if you have any questions"
            aria-label="lock explaination message"
            show={isSignatureLocked}
          >
            <button
              disabled={isSignatureLocked}
              onClick={handleAddEnvironmentalFooter}
              className={styles['addons-box-header-add-button']}
              data-is-locked={isSignatureLocked}
            >
              {isSignatureLocked ? <FontAwesomeIcon icon={faLock} /> : 'Add'}
            </button>
          </Tooltip>
        )}

        {emailSignature.environmentalFooter && !isSignatureLocked && (
          <button
            onClick={handleRemoveEnvironmentalFooter}
            className={styles['addons-box-header-delete-button']}
          >
            <FontAwesomeIcon size="1x" icon={faTrash} />
          </button>
        )}
      </EmailSignatureEditorAddonsBoxHeader>
      {emailSignature.environmentalFooter && <EmailSignatureEditorAddonsBoxHeaderDivider />}

      <EmailSignatureEditorAddonsBoxBody collapsed={!emailSignature.environmentalFooter}>
        <div className={styles['addons-selector-textarea-field']}>
          <div className={styles['addons-selector-field-container']}>
            <div className={styles['addons-field-label']}>Footer options</div>
            <Select
              //https://github.com/JedWatson/react-select/issues/2629
              id={'very long passage to fix the ssr bug in react-select'}
              instanceId={'very long passage to fix the ssr bug in react-select'}
              maxMenuHeight={200}
              components={{
                // biome-ignore lint/correctness/noNestedComponentDefinitions: historic
                IndicatorSeparator: () => null,
              }}
              styles={{
                container: () => ({ width: '100%' }),
                control: (base) => ({
                  ...base,
                  height: '2rem',
                  borderRadius: '5px',
                  width: '100%',
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (base) => ({
                  ...base,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }),
              }}
              value={selectedFooterOption}
              options={ENVIRONMENTAL_FOOTER_OPTIONS}
              onChange={handleSelectedFooterOptionChange}
              menuPortalTarget={menuPortalTarget}
              isDisabled={isSignatureLocked}
            />
          </div>
          <div className={environmentalStyles['addons-selector-environmental-textarea']}>
            <Input
              hideAllErrorMessages
              multiLine
              value={emailSignature.environmentalFooter?.value ?? ''}
              onChange={handleEnvironmentalFooterValueChange}
              disabled={isSignatureLocked}
            />
          </div>
        </div>
        <div className={styles['addons-field']}>
          <div className={styles['addons-field-label']}>Footer icon</div>
          <FormSelectorGroup
            className={styles['addons-form-selector-icons-group']}
            selectedValue={
              emailSignature.environmentalFooter?.baseIcon ?? ENVIRONMENTAL_FOOTER_ICON_OPTIONS[0]
            }
            size="sm"
            onChange={handleEnvironmentalFooterBaseIconChange}
          >
            {ENVIRONMENTAL_FOOTER_ICON_OPTIONS.map((offeredIcon, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: historic
              <div key={index} className={styles['addons-form-icon-selector']}>
                <FormSelector disabled={isSignatureLocked} value={offeredIcon}>
                  <EmailSignatureIcon icon={offeredIcon} size="1x"></EmailSignatureIcon>
                </FormSelector>
              </div>
            ))}
          </FormSelectorGroup>
        </div>

        {isSignatureLocked && (
          <EmailSignatureLockLayer message="You cannot edit the environment footer, please contact your admin if you have any questions" />
        )}
      </EmailSignatureEditorAddonsBoxBody>
    </EmailSignatureEditorAddonsBox>
  );
};

export default EmailSignatureEditorAddonsEnvironmentalBox;
